import React, { useEffect, useMemo, useRef, useState } from 'react';

import { makeStyles, MenuItem, TextField, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import DialogViewWrapper from 'components/DialogViewWrapper';
import Loader from 'components/Loader';
import RelatedContentOneColumn from 'components/RelatedContentOneColumn';
import guidesEndpoints from 'config/api/guides';
import { parseContentItemForFE } from 'config/api/selfTests/parsers';
import { logObjects } from 'config/constants/logs';
import checkAccess from 'config/permissions/checkAccess';
import STAGES_WITH_ENABLED_FUNCTIONALITIES from 'config/permissions/STAGES_WITH_ENABLED_FUNCTIONALITIES';
import useApiCall from 'hooks/useApiCall';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import useLoadingState from 'hooks/useLoadingState';
import useOrganizationPaymentStageInfo from 'hooks/useOrganizationPaymentStageInfo/useOrganizationPaymentStageInfo';
import useRedirectFromQuery from 'hooks/useRedirectFromQuery/useRedirectFromQuery';
import useSendingState from 'hooks/useSendingState';
import guide_messages from 'messages/guide_messages';
import info_boxes_messages from 'messages/info_boxes_messages';
import table_messages from 'messages/table_messages';
import validation_messages from 'messages/validation_messages';
import WIDE_SINGLE_COLUMN_CONTENT_WIDTH from 'pages/wideSingleColumnLayout';
import PATHS from 'router/PATHS';
import logEvent from 'services/logEvent';

import PageWithInfoBox from '../PageWithInfoBox/PageWithInfoBox';

import GuideAction from './_components/GuideAction';
import GuideActionButtons from './_components/GuideActionButtons';
import GuideComments from './_components/GuideComments';
import GuideSteps from './_components/GuideSteps';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  headImage: {
    maxWidth: '100%',
  },
  progressRatioHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.text.secondaryColor}`,
  },
  progressRatio: {
    textAlign: 'right',
    color: theme.palette.text.secondaryColor,
    fontWeight: 500,
  },
  metadataPanel: {
    display: 'grid',
    gridGap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
  },
  metadataFields: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridGap: theme.spacing(2),
  },
  relatedContentWrapper: {
    width: '100%',
    maxWidth: WIDE_SINGLE_COLUMN_CONTENT_WIDTH,
    margin: '0 auto',
  },
}));

const FORM_ID = 'guide_form';

const SingleGuidePage = () => {
  const { guide_id, version_id, progress_id } = useParams();
  const { getRedirectValue } = useRedirectFromQuery();
  const history = useHistory();
  const { t } = useTranslation();
  const { apiCall } = useApiCall();
  const { loading, setLoading, setLoaded } = useLoadingState();
  const { sending, setSent, setSending } = useSendingState(false);
  const [localProgressId, setLocalProgressId] = useState(progress_id);
  const [isRoot, setIsRoot] = useState(true);
  const [guideData, setGuideData] = useState();
  const [currentStepId, setCurrentStepId] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [availableSteps, setAvailableSteps] = useState(null);
  const [progressRatio, setProgressRatio] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState(false);
  const [progressMetadata, setProgressMetadata] = useState({ title: '', asset_id: '', assetChanged: false });
  const assetsRequestRef = useRef(0);
  const guideRequestRef = useRef(0);

  const { stage } = useOrganizationPaymentStageInfo();

  const hasAccess = useMemo(() => checkAccess([stage], STAGES_WITH_ENABLED_FUNCTIONALITIES), [stage]);

  /**
   * save user choices on step change
   * @param actions
   * @param comment
   * @returns {Promise<void>}
   */
  const saveUserData = async ({ actions, comment }, progressId = localProgressId, stepId = currentStepId) => {
    setSending();
    const actionsToUpdate = Object.values(actions).filter(action => action?.guide_action_id);
    const actionsToRemove = Object.values(actions).filter(action => action?.id && !action.guide_action_id);
    const shouldUpdateComment = !isNil(comment) && progressId && stepId;

    await Promise.all([
      ...actionsToUpdate.map(data => apiCall(guidesEndpoints.updateAction(), { data: { ...data, guide_progress_id: progressId } })),
      ...actionsToRemove.map(({ id }) => apiCall(guidesEndpoints.removeAction(id))),
      shouldUpdateComment &&
        apiCall(guidesEndpoints.updateStep(), { data: { comment, guide_progress_id: progressId, guide_step_id: stepId } }),
    ]);
    setSent();
  };

  const resetForm = () => {
    /* eslint-disable @typescript-eslint/no-use-before-define */
    if (formik) formik.resetForm();
  };

  const onSubmit = async data => {
    if (data.nextStepId) {
      const progressId = await ensureProgress(data.nextStepId);
      if (!progressId) return;
      await saveUserData(data, progressId, currentStepId);
      const metadataSaved = await saveProgressMetadata(data.nextStepId, progressId);
      if (!metadataSaved) return;
      setCurrentStepId(data.nextStepId);
      return;
    }
    await saveUserData(data);
    await saveProgressMetadata(currentStepId);
  };

  const onPrev = async prevId => {
    const { actions } = currentStep;
    if (actions && actions.length) {
      actions.forEach(({ user_data }) => {
        if (user_data) apiCall(guidesEndpoints.removeAction(user_data.id));
      });
    }
    await apiCall(guidesEndpoints.updateStep(), {
      data: { comment: '', guide_progress_id: localProgressId, guide_step_id: currentStepId },
    });
    const metadataSaved = await saveProgressMetadata(prevId);
    if (!metadataSaved) return;
    setCurrentStepId(prevId);
  };

  const formik = useFormik({
    initialValues: {
      nextStep: {},
      nextStepId: '',
      comment: '',
      actions: {},
    },
    validationSchema: yup.object({
      nextStepId: yup.string().required(t(validation_messages.required)),
    }),
    validateOnMount: true,
    onSubmit,
  });

  const openIncidentCreator = () => {
    history.push(
      `${PATHS.INCIDENT_REPORT}/?localProgressId=${localProgressId}&fromGuide=true&redirect_url=${getRedirectValue({
        redirectKey: 'redirect_url',
        fallback: PATHS.GUIDES,
      })}`,
    );
  };

  const assetSelectionMode = guideData?.asset_selection_mode || 'none';
  const assetSelectionEnabled = !guideData?.is_incident && assetSelectionMode !== 'none';

  const getProgressPayload = (stepId, forCreate = false) => {
    const payload = {
      guide_root_id: guide_id,
      guide_step_id: stepId,
    };

    if (!guideData?.is_incident) {
      payload.title = progressMetadata.title || '';
      if (assetSelectionEnabled && (forCreate || progressMetadata.assetChanged)) {
        payload.asset_id = progressMetadata.asset_id || null;
      }
    }

    return payload;
  };

  const ensureProgress = async stepId => {
    if (localProgressId) return localProgressId;
    const response = await apiCall(guidesEndpoints.createProgress(), { data: getProgressPayload(stepId, true) });
    const createdProgressData = response?.data;
    if (response?.status < 200 || response?.status >= 300 || !createdProgressData?.id) return null;
    setLocalProgressId(createdProgressData.id);
    setProgressData(createdProgressData);
    setProgressMetadata(current => ({
      ...current,
      asset_id: createdProgressData?.asset?.id || current.asset_id,
      assetChanged: false,
    }));
    return createdProgressData.id;
  };

  const saveProgressMetadata = async (stepId = currentStepId, progressId = localProgressId) => {
    if (!progressId || !stepId || progressData?.is_completed || guideData?.is_incident) return true;
    const response = await apiCall(guidesEndpoints.updateProgress(progressId), { data: getProgressPayload(stepId) });
    if (response?.status < 200 || response?.status >= 300) return false;
    const { data } = response;
    setProgressData(data);
    setProgressMetadata(current => ({
      ...current,
      asset_id: data?.asset?.id || '',
      assetChanged: false,
    }));
    return true;
  };

  const closeWithSave = async isIncident => {
    const progressId = await ensureProgress(currentStepId);
    if (!progressId) return;
    const metadataSaved = await saveProgressMetadata(currentStepId, progressId);
    if (!metadataSaved) return;
    await saveUserData(formik.values, progressId, currentStepId);
    if (isIncident) {
      openIncidentCreator();
      return;
    }
    history.push(PATHS.ROOT);
  };

  /**
   * update previously saved progress
   * @returns {Promise<void>}
   */
  const updateInitialProgress = async requestId => {
    setIsRoot(false);

    const { data: fetchedProgressData } = await apiCall(guidesEndpoints.getProgress(localProgressId));
    if (guideRequestRef.current !== requestId) return;
    const id = fetchedProgressData?.guide_step?.id;
    if (id) setCurrentStepId(id);
    setProgressRatio(fetchedProgressData?.guide_step?.progress_ratio);
    setProgressData(fetchedProgressData);
    setProgressMetadata({
      title: fetchedProgressData?.title || '',
      asset_id: fetchedProgressData?.asset?.id || '',
      assetChanged: false,
    });
  };

  const getSelectableAssets = async concreteVersionId => {
    const requestId = assetsRequestRef.current + 1;
    assetsRequestRef.current = requestId;
    setAssets([]);
    setAssetsError(false);
    setAssetsLoading(true);
    const response = await apiCall(guidesEndpoints.getSelectableAssets(concreteVersionId), {}, { showError: false });
    if (assetsRequestRef.current !== requestId) return;
    if (response?.status >= 200 && response?.status < 300 && Array.isArray(response.data)) {
      setAssets(response.data);
    } else {
      setAssetsError(true);
    }
    setAssetsLoading(false);
  };

  const setRootData = steps => {
    setIsRoot(true);
    setCurrentStepId(null);
    setCurrentStep(null);
    setAvailableSteps(steps || guideData?.steps);
    setProgressRatio(steps[0]?.progress_ratio || guideData?.steps[0]?.progress_ratio || 0);
  };

  const populateUserData = ({ actions, user_data: step_user_data }) => {
    const parsedActions = {};
    const comment = step_user_data?.comment;
    if (actions) {
      actions.forEach(({ user_data, id: actionId }) => {
        parsedActions[actionId] = user_data;
      });
    }
    /* eslint-disable @typescript-eslint/no-use-before-define */
    formik.setFieldValue('actions', parsedActions);
    if (!isNil(comment)) formik.setFieldValue('comment', comment);
    /* eslint-enable @typescript-eslint/no-use-before-define */
  };

  /**
   * get general schema of guide and update
   * @returns {Promise<void>}
   */
  const getGuideData = async concreteVersionId => {
    const requestId = guideRequestRef.current + 1;
    guideRequestRef.current = requestId;
    setLoading();
    const response = await apiCall(guidesEndpoints.get(concreteVersionId));
    if (guideRequestRef.current !== requestId) return;
    if (response?.status < 200 || response?.status >= 300 || !response?.data) {
      setLoaded();
      return;
    }
    const { data } = response;
    setGuideData(data);
    if (localProgressId) {
      await updateInitialProgress(requestId);
    } else {
      if (!data.is_incident) {
        setProgressMetadata(current => ({ ...current, title: data.heading || '' }));
      }
      setRootData(data.steps);
    }
    if (guideRequestRef.current === requestId) setLoaded();
  };

  /**
   * get user choices on mount and step change
   * @param stepId
   * @returns {Promise<void>}
   */
  const getStepData = async stepId => {
    const params = {};
    if (localProgressId) params.guide_progress_id = localProgressId;
    const { data } = await apiCall(guidesEndpoints.getStep(stepId, params));
    const newSteps = data.children;
    setAvailableSteps(newSteps);
    setCurrentStep(data);
    populateUserData(data);
    setIsRoot(false);
    setProgressRatio(data.progress_ratio);
  };

  /**
   * save user progress ONLY on step change
   * @param stepId
   * @returns {Promise<void>}
   */
  const saveUserProgress = async stepId => {
    const data = getProgressPayload(stepId);
    const progressId = stepId ? await ensureProgress(stepId) : localProgressId;

    if (stepId && progressId) {
      const { data: updatedProgressData } = await apiCall(guidesEndpoints.updateProgress(progressId), { data });
      setProgressData(updatedProgressData);
    } else if (progressId) {
      setLocalProgressId(null);
      await apiCall(guidesEndpoints.deleteProgress(progressId));
      const urlParams = new URLSearchParams(history.location.search);
      history.push(`${PATHS.GUIDES}/${guide_id}/${version_id}/?${decodeURI(urlParams.toString())}`);
    }
  };

  useEffect(() => {
    resetForm();
    if (currentStepId) getStepData(currentStepId);
  }, [currentStepId]);

  useDidUpdateEffect(() => {
    saveUserProgress(currentStepId);
    logEvent(logObjects.GUIDE_STEP);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    formik.setFieldValue('nextStepId', '');
  }, [currentStepId]);

  useEffect(() => {
    getGuideData(version_id);
    logEvent(logObjects.GUIDE);
    return () => {
      assetsRequestRef.current += 1;
      guideRequestRef.current += 1;
    };
  }, [version_id]);

  useEffect(() => {
    assetsRequestRef.current += 1;
    setAssets([]);
    setAssetsError(false);
    setAssetsLoading(false);
    setGuideData(undefined);
    setProgressData(null);
    setProgressMetadata(current => ({ ...current, title: '', asset_id: '', assetChanged: false }));
  }, [version_id]);

  useEffect(() => {
    if (String(guideData?.version_id) === String(version_id) && assetSelectionEnabled) getSelectableAssets(version_id);
  }, [assetSelectionEnabled, guideData?.version_id, version_id]);

  useEffect(() => {
    if (availableSteps?.length === 1) {
      formik.setFieldValue('nextStep', availableSteps[0]);
      formik.setFieldValue('nextStepId', availableSteps[0].id);
      setTimeout(formik.validateForm, 0);
    }
  }, [availableSteps]);

  const isNextDisabled = useMemo(() => {
    if (!currentStep) return false;
    if (availableSteps?.length <= 1) return false;
    return !formik.isValid && !currentStep.is_leaf;
  }, [formik.isValid, currentStep]);

  const styles = useStyles();
  const assetOptions = useMemo(() => {
    const selectedAssetInOptions = assets.some(asset => String(asset.id) === String(progressMetadata.asset_id));
    const selectedProgressAsset =
      progressData?.asset && String(progressData.asset.id) === String(progressMetadata.asset_id) ? progressData.asset : null;
    return !selectedAssetInOptions && selectedProgressAsset ? [selectedProgressAsset, ...assets] : assets;
  }, [assets, progressData?.asset, progressMetadata.asset_id]);
  const metadataReadOnly = !!progressData?.is_completed;
  const historicalAssetName = progressData?.asset_name || progressData?.asset_name_snapshot || progressData?.asset?.name || '';
  let assetHelperText;
  if (assetsLoading) assetHelperText = t(...guide_messages.progress_assets_loading);
  else if (assetsError) assetHelperText = t(...guide_messages.progress_assets_error);
  else if (assetOptions.length === 0) assetHelperText = t(...guide_messages.progress_assets_empty);

  if (!hasAccess)
    return (
      <PageWithInfoBox
        content={t(info_boxes_messages.restricted_access_page.content)}
        title={t(info_boxes_messages.restricted_access_page.title)}
      />
    );

  return (
    <>
      <DialogViewWrapper contentSize={WIDE_SINGLE_COLUMN_CONTENT_WIDTH} title={t(...guide_messages.guide_page_title)}>
        {loading ? (
          <Loader inner />
        ) : (
          <form className={styles.wrapper} id={FORM_ID} onSubmit={formik.handleSubmit}>
            {currentStep && (
              <div className={styles.progressRatioHeader}>
                <Typography>{guideData?.service_area.name}</Typography>
                {progressRatio && <Typography className={styles.progressRatio}>{`${progressRatio}%`}</Typography>}
              </div>
            )}
            <Typography className={styles.title} component='h2' variant='h2'>
              {guideData?.heading}
            </Typography>
            {!guideData?.is_incident && (
              <div className={styles.metadataPanel}>
                <Typography component='h3' variant='h3'>
                  {t(...guide_messages.progress_metadata)}
                </Typography>
                <div className={styles.metadataFields}>
                  <TextField
                    disabled={metadataReadOnly}
                    fullWidth
                    label={t(...guide_messages.progress_title)}
                    onChange={event => setProgressMetadata(current => ({ ...current, title: event.target.value }))}
                    value={progressMetadata.title}
                    variant='outlined'
                  />
                  {metadataReadOnly && historicalAssetName && (
                    <TextField
                      disabled
                      fullWidth
                      label={t(...guide_messages.progress_asset)}
                      value={historicalAssetName}
                      variant='outlined'
                    />
                  )}
                  {!metadataReadOnly && assetSelectionEnabled && (
                    <TextField
                      disabled={assetsLoading || assetsError}
                      fullWidth
                      helperText={assetHelperText}
                      label={t(...guide_messages.progress_asset)}
                      onChange={event => setProgressMetadata(current => ({ ...current, asset_id: event.target.value, assetChanged: true }))}
                      select
                      value={progressMetadata.asset_id}
                      variant='outlined'
                    >
                      <MenuItem value=''>{t(...table_messages.no_data_cell)}</MenuItem>
                      {assetsLoading && (
                        <MenuItem disabled value='__loading'>
                          {t(...guide_messages.progress_assets_loading)}
                        </MenuItem>
                      )}
                      {assetsError && (
                        <MenuItem disabled value='__error'>
                          {t(...guide_messages.progress_assets_error)}
                        </MenuItem>
                      )}
                      {!assetsLoading && !assetsError && assetOptions.length === 0 && (
                        <MenuItem disabled value='__empty'>
                          {t(...guide_messages.progress_assets_empty)}
                        </MenuItem>
                      )}
                      {assetOptions.map(asset => (
                        <MenuItem key={asset.id} value={asset.id}>
                          {asset.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </div>
              </div>
            )}
            {isRoot && guideData?.image_url && <img alt={guideData?.heading} className={styles.headImage} src={guideData.image_url} />}
            <GuideAction currentStep={currentStep} description={guideData?.description} formik={formik} isRoot={isRoot} />
            {!isRoot && <GuideComments formik={formik} />}
            {availableSteps?.length > 1 && (
              <GuideSteps availableSteps={availableSteps} currentStep={currentStep} formik={formik} isRoot={isRoot} />
            )}
            <GuideActionButtons
              closeWithSave={closeWithSave}
              currentStep={currentStep}
              disabled={isNextDisabled}
              form={FORM_ID}
              isIncident={guideData?.is_incident}
              onPrev={onPrev}
              setRootData={setRootData}
              showLoader={sending}
            />
          </form>
        )}
      </DialogViewWrapper>
      {isRoot && (
        <div className={styles.relatedContentWrapper} data-content-size={WIDE_SINGLE_COLUMN_CONTENT_WIDTH}>
          <RelatedContentOneColumn
            articles={guideData?.related_articles && parseContentItemForFE(guideData.related_articles)}
            guides={guideData?.related_guides && parseContentItemForFE(guideData.related_guides)}
            isLoading={loading}
            selfTests={guideData?.related_self_tests && parseContentItemForFE(guideData.related_self_tests)}
          />
        </div>
      )}
    </>
  );
};

export default SingleGuidePage;
